(function () {
  const STORAGE_KEY = 'mysimplicated:wishlist';

  function readAll() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  }

  function writeAll(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items } }));
  }

  function has(id) {
    return readAll().some((item) => item.id === id);
  }

  function toggle(item) {
    const items = readAll();
    const index = items.findIndex((existing) => existing.id === item.id);
    const added = index === -1;
    if (added) {
      items.push(item);
    } else {
      items.splice(index, 1);
    }
    writeAll(items);
    return added;
  }

  function remove(id) {
    writeAll(readAll().filter((item) => item.id !== id));
  }

  window.Wishlist = { readAll, has, toggle, remove };

  function trackAddToWishlist(button) {
    const value = parseFloat(button.dataset.productPriceAmount) || 0;
    const currency = button.dataset.currency || '';
    const item = {
      item_id: button.dataset.variantId || button.productId,
      item_name: button.dataset.productTitle,
      price: value,
      quantity: 1,
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'add_to_wishlist',
      item_name: item.item_name,
      ecommerce: { currency, value, items: [item] },
    });
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'add_to_wishlist', {
        currency,
        value,
        item_name: item.item_name,
        items: [item],
      });
    }
  }

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.productId = this.dataset.productId;
      this.updateState();
      this.onClick = this.onClick.bind(this);
      this.updateState = this.updateState.bind(this);
      this.addEventListener('click', this.onClick);
      document.addEventListener('wishlist:change', this.updateState);
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this.updateState);
    }

    updateState() {
      const active = Wishlist.has(this.productId);
      this.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      const added = Wishlist.toggle({
        id: this.productId,
        handle: this.dataset.productHandle,
        url: this.dataset.productUrl,
        title: this.dataset.productTitle,
        price: this.dataset.productPrice,
        image: this.dataset.productImage,
        variantId: this.dataset.variantId,
      });
      if (added) {
        trackAddToWishlist(this);
        const drawer = document.getElementById('WishlistDrawer');
        if (drawer) drawer.open(this);
      }
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.update = this.update.bind(this);
      this.update();
      document.addEventListener('wishlist:change', this.update);
      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_KEY) this.update();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this.update);
    }

    update() {
      const count = Wishlist.readAll().length;
      const countEl = this.querySelector('[data-wishlist-count-value]');
      if (countEl) countEl.textContent = count;
      this.hidden = count === 0;
    }
  }

  class WishlistDrawerOpener extends HTMLElement {
    connectedCallback() {
      const link = this.querySelector('a');
      if (!link) return;
      link.addEventListener('click', (event) => {
        const drawer = document.querySelector(this.getAttribute('data-drawer'));
        if (!drawer) return;
        event.preventDefault();
        drawer.open(link);
      });
    }
  }

  class WishlistDrawer extends HTMLElement {
    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);

      this.backdrop = this.querySelector('.wishlist-drawer__backdrop');
      this.closeButton = this.querySelector('.wishlist-drawer__close');
      this.emptyState = this.querySelector('[data-wishlist-drawer-empty]');
      this.list = this.querySelector('[data-wishlist-drawer-list]');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      const stringsEl = document.getElementById('WishlistDrawerStrings');
      this.strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};

      this.backdrop.addEventListener('click', () => this.close());
      this.closeButton.addEventListener('click', () => this.close());
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });

      this.render = this.render.bind(this);
      this.render();
      document.addEventListener('wishlist:change', this.render);
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this.render);
    }

    open(opener) {
      this.openedBy = opener;
      this.render();
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      if (this.openedBy) this.openedBy.focus();
    }

    render() {
      const items = readAll();
      this.emptyState.hidden = items.length > 0;
      this.list.hidden = items.length === 0;
      this.list.innerHTML = '';

      items.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'wishlist-drawer__item';
        li.innerHTML = `
          <a href="${item.url}" class="wishlist-drawer__item-image">
            <img src="${item.image}" alt="" loading="lazy" width="120" height="180">
          </a>
          <div class="wishlist-drawer__item-info">
            <a href="${item.url}" class="wishlist-drawer__item-title">${item.title}</a>
            <span class="wishlist-drawer__item-price">${item.price}</span>
            <button type="button" class="wishlist-drawer__item-add button button--secondary" data-add-id="${item.id}">
              <span>${this.strings.add_to_cart || 'Add to cart'}</span>
            </button>
            <button type="button" class="wishlist-drawer__item-remove" data-remove-id="${item.id}">${this.strings.remove || 'Remove'}</button>
          </div>
        `;
        li.querySelector('[data-remove-id]').addEventListener('click', () => {
          remove(item.id);
        });
        const addButton = li.querySelector('[data-add-id]');
        addButton.addEventListener('click', () => this.openSizePicker(item, addButton));
        this.list.appendChild(li);
      });
    }

    openSizePicker(item, button) {
      if (button.classList.contains('loading')) return;
      button.classList.add('loading');

      fetch(`/products/${item.handle}.js`)
        .then((response) => response.json())
        .then((product) => {
          const existing = document.getElementById('WishlistSizeDrawer');
          if (existing) existing.remove();

          const optionsHtml = product.variants.map((variant) => `
            <button type="button" class="size-drawer__option${variant.available ? '' : ' size-drawer__option--sold-out'}" data-variant-id="${variant.id}"${variant.available ? '' : ' disabled'}>
              ${variant.title}
            </button>
          `).join('');

          const wrapper = document.createElement('div');
          wrapper.innerHTML = `
            <size-drawer id="WishlistSizeDrawer" class="size-drawer">
              <div class="size-drawer__backdrop"></div>
              <div class="size-drawer__panel" role="dialog" aria-modal="true" tabindex="-1">
                <div class="size-drawer__options">${optionsHtml}</div>
              </div>
            </size-drawer>
          `;
          document.body.appendChild(wrapper.firstElementChild);
          document.getElementById('WishlistSizeDrawer').open(button);
          document.addEventListener('cart:updated', () => this.close(), { once: true });
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          button.classList.remove('loading');
        });
    }
  }

  if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
  if (!customElements.get('wishlist-drawer-opener')) customElements.define('wishlist-drawer-opener', WishlistDrawerOpener);
  if (!customElements.get('wishlist-drawer')) customElements.define('wishlist-drawer', WishlistDrawer);

  function initScrollProgressBar(track) {
    if (track.dataset.scrollProgressReady) return;
    const prevEl = track.previousElementSibling;
    const row = prevEl && prevEl.querySelector ? (prevEl.querySelector('ul.list-unstyled') || prevEl) : prevEl;
    const fill = track.querySelector('[data-scroll-progress-fill]');
    if (!row || !fill) return;
    track.dataset.scrollProgressReady = 'true';

    function update() {
      const maxScroll = row.scrollWidth - row.clientWidth;
      const visibleRatio = Math.min(1, row.clientWidth / row.scrollWidth);
      fill.style.width = (visibleRatio * 100) + '%';
      if (maxScroll <= 0) {
        fill.style.transform = 'translateX(0)';
        return;
      }
      const scrollRatio = row.scrollLeft / maxScroll;
      const trackWidth = track.clientWidth;
      const fillWidth = visibleRatio * trackWidth;
      const maxTranslate = trackWidth - fillWidth;
      fill.style.transform = 'translateX(' + (scrollRatio * maxTranslate) + 'px)';
    }

    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(row);
    }

    // Read scrollWidth/clientWidth after the current layout/paint cycle
    // settles, not synchronously on init — reading too early (e.g. before a
    // deferred stylesheet applies the flex layout) can catch clientWidth ===
    // scrollWidth and freeze the fill at 100% until the user's first scroll.
    requestAnimationFrame(function () {
      requestAnimationFrame(update);
    });
  }

  function scanForScrollProgressBars() {
    document.querySelectorAll('[data-scroll-progress-track]').forEach(initScrollProgressBar);
  }

  scanForScrollProgressBars();
  window.setInterval(scanForScrollProgressBars, 800);

  function initArrows(wrapper) {
    if (wrapper.dataset.arrowsReady) return;
    const row = wrapper.querySelector('ul.list-unstyled');
    const prev = wrapper.querySelector('[data-scroll-arrow="prev"]');
    const next = wrapper.querySelector('[data-scroll-arrow="next"]');
    if (!row || !prev || !next) return;
    wrapper.dataset.arrowsReady = 'true';

    function step() {
      const item = row.querySelector('li');
      return item ? item.getBoundingClientRect().width + 19 : row.clientWidth;
    }

    prev.addEventListener('click', () => row.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => row.scrollBy({ left: step(), behavior: 'smooth' }));
  }

  function scanForArrows() {
    document.querySelectorAll('.complete-look-row-wrapper').forEach(initArrows);
  }

  scanForArrows();
  window.setInterval(scanForArrows, 800);

  var HIDDEN_WIDGET_TEXT = 'Add a 2nd product and get 15% off';

  function hideStaleUpsellWidget() {
    var candidates = document.querySelectorAll('div');
    for (var i = 0; i < candidates.length; i++) {
      var node = candidates[i];
      if (node.children.length !== 0) continue;
      if (node.textContent.trim() !== HIDDEN_WIDGET_TEXT) continue;
      if (node.closest('.complementary-products')) continue;
      var target = node.parentElement && node.parentElement.parentElement;
      if (!target) continue;
      if (target.querySelector('button[type="submit"], input[type="submit"]')) continue;
      target.style.display = 'none';
    }
  }

  hideStaleUpsellWidget();
  window.setInterval(hideStaleUpsellWidget, 800);
})();
